'use client';

import { useState } from 'react';
import Image from 'next/image';
import type { ProductDetailProps, CommerceVariant } from '@routeroyal/theme-core';
import { RouteRoyalProductCard } from './ProductCard';

export function RouteRoyalProductDetailView({
  product,
  locale = 'en',
  relatedProducts = [],
  onAddToCart,
  className = '',
}: ProductDetailProps) {
  const [selectedVariant, setSelectedVariant] = useState<CommerceVariant | undefined>(
    product.variants[0],
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    if (selectedVariant?.optionValues) {
      for (const ov of selectedVariant.optionValues) {
        initial[ov.optionId] = ov.value;
      }
    }
    return initial;
  });

  const currency = selectedVariant?.currency ?? product.currency ?? 'GBP';

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);

  const handleOptionChange = (optionId: string, value: string) => {
    const next = { ...selectedOptions, [optionId]: value };
    setSelectedOptions(next);

    const match = product.variants.find((v) =>
      v.optionValues?.every((ov) => next[ov.optionId] === ov.value),
    );
    if (match) setSelectedVariant(match);
  };

  const images = [
    selectedVariant?.imageUrl,
    product.thumbnailUrl,
  ].filter(Boolean) as string[];

  return (
    <div className={`max-w-6xl mx-auto ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div className="space-y-4">
          <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
            {images[0] ? (
              <Image
                src={images[0]}
                alt={product.title}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
                priority
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-300">
                <svg className="w-24 h-24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="flex flex-col">
          {product.vendor && (
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">
              {product.vendor}
            </p>
          )}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.title}</h1>
          {product.subtitle && (
            <p className="text-lg text-gray-600 mb-4">{product.subtitle}</p>
          )}

          {/* Price */}
          <div className="flex items-baseline gap-3 mb-6">
            <span className="text-2xl font-bold text-gray-900">
              {formatPrice(selectedVariant?.price ?? product.minPrice ?? 0)}
            </span>
            {selectedVariant?.compareAtPrice != null &&
              selectedVariant.compareAtPrice > selectedVariant.price && (
                <span className="text-lg text-gray-400 line-through">
                  {formatPrice(selectedVariant.compareAtPrice)}
                </span>
              )}
          </div>

          {/* Rating */}
          {product.averageRating != null && product.reviewCount > 0 && (
            <div className="flex items-center gap-2 mb-6">
              <div className="flex text-yellow-500">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className={star <= Math.round(product.averageRating!) ? '' : 'opacity-30'}>
                    ★
                  </span>
                ))}
              </div>
              <span className="text-sm text-gray-500">
                {product.averageRating.toFixed(1)} ({product.reviewCount} reviews)
              </span>
            </div>
          )}

          {/* Options */}
          {product.options && product.options.length > 0 && (
            <div className="space-y-4 mb-6">
              {product.options.map((option) => (
                <div key={option.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {option.name}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {option.values.map((val) => (
                      <button
                        key={val.id}
                        onClick={() => handleOptionChange(option.id, val.value)}
                        className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                          selectedOptions[option.id] === val.value
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        {val.value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quantity */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity
            </label>
            <div className="flex items-center border border-gray-300 rounded-lg w-fit">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                −
              </button>
              <span className="px-4 py-2 text-center min-w-[3rem] font-medium">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                +
              </button>
            </div>
          </div>

          {/* Add to cart */}
          {onAddToCart && selectedVariant && (
            <button
              onClick={() => onAddToCart(selectedVariant.id, quantity)}
              disabled={!selectedVariant.available}
              className="w-full rounded-xl bg-gray-900 px-6 py-3.5 text-base font-semibold text-white hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {selectedVariant.available ? 'Add to Cart' : 'Out of Stock'}
            </button>
          )}

          {/* Description */}
          {product.descriptionHtml ? (
            <div
              className="mt-8 prose prose-gray max-w-none"
              dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
            />
          ) : product.description ? (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Description
              </h3>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            You Might Also Like
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedProducts.slice(0, 4).map((rp) => (
              <RouteRoyalProductCard
                key={rp.id}
                product={rp}
                locale={locale}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
